import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { ArrowUpRight, Loader2, Star, ChevronLeft, ChevronRight, Check, ArrowRight, Volume2, VolumeX, Play, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignupForm from "@/components/SignupForm";

const LIME = "#ccff00";
const HERO_FALLBACK = "https://static.prod-images.emergentagent.com/jobs/0a46be76-d1d2-4156-a6df-b2b75b9d7a3d/images/41cf460b224a70a957e8ff1e9d67ba4997e3f00879c1900c9955275e861bd155.jpeg";
const HERO_VIDEO = "https://customer-assets-agu9un31.emergentagent.net/job_store-dashboard-69/artifacts/3agjxuiy_StoreIG%20Hero.mp4";
const DEFAULT_STEPS = [
  { title: "Create your account", text: "Sign up with email or Google in seconds." },
  { title: "Add your products", text: "Upload photos, set prices — done." },
  { title: "Share & start selling", text: "Real Stripe checkout from day one." },
];
import { openCookieSettings } from "@/lib/consent";
const LEGAL_LINKS = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "cookies", label: "Cookie Policy" },
  { slug: "refunds", label: "Returns & Refunds" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "imprint", label: "Contact & Imprint" },
];
const DEFAULT_DISCLAIMER = "StoreIG is an independent platform and is not affiliated with, endorsed by, sponsored by, or in any way officially connected to Instagram or Meta Platforms, Inc.";
const anton = { fontFamily: "Anton, Archivo, sans-serif" };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.11, delayChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 90, damping: 18 } } };
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

export default function DirectoryV2({ content, stores, loading }) {
  const [signupOpen, setSignupOpen] = useState(false);
  const [subscribing, setSubscribing] = useState(null);
  const [muted, setMuted] = useState(true);
  const [catFilter, setCatFilter] = useState("All");
  const videoRef = useRef(null);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) { v.volume = 1; v.play().catch(() => {}); }
    setMuted(next);
  };

  const scrollStores = () => document.getElementById("stores")?.scrollIntoView({ behavior: "smooth" });

  const handlePlan = async (p, i) => {
    const amount = parseFloat(String(p.price).replace(/[^\d.,]/g, "").replace(",", "."));
    if (content.subscriptions_enabled && amount > 0) {
      setSubscribing(i);
      try {
        const res = await api.post("/payments/subscribe", { plan_index: i, plan_id: p.id || "", origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setSubscribing(null); }
    } else { setSignupOpen(true); }
  };

  const featuredFirst = [...stores].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  const newStores = featuredFirst.slice(0, 6);
  const featuredStores = stores.filter((s) => s.featured);
  const categories = Array.from(new Set(stores.map((s) => s.category).filter(Boolean)));
  const catStores = catFilter === "All" ? stores : stores.filter((s) => s.category === catFilter);
  const signupSteps = content.signup_steps?.length ? content.signup_steps : DEFAULT_STEPS;

  return (
    <div className="relative min-h-screen text-white overflow-x-clip selection:bg-[#ccff00] selection:text-black"
      style={{ background: "radial-gradient(1200px 720px at 12% -8%, rgba(204,255,0,0.06), transparent 55%), radial-gradient(900px 600px at 100% 2%, rgba(80,120,90,0.10), transparent 50%), #0a0a0b" }}>
      {/* Nav */}
      <header className="relative z-20 max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <div style={anton} className="text-2xl tracking-wide" data-testid="directory-brand">
          STORE<span style={{ color: LIME }}>IG</span>
        </div>
        <nav className="hidden lg:flex items-center gap-9 text-sm font-medium text-white/70">
          <button onClick={scrollStores} className="hover:text-white transition-colors">Stores</button>
          <button onClick={scrollStores} className="hover:text-white transition-colors">Categories</button>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <button onClick={() => setSignupOpen(true)} className="hover:text-white transition-colors">For Merchants</button>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="admin-cta" className="text-sm font-semibold text-white/80 hover:text-white transition-colors">Log in</Link>
          <button onClick={() => setSignupOpen(true)} data-testid="signup-cta"
            className="text-sm font-semibold border border-white/30 rounded-full px-5 py-2 hover:border-white hover:bg-white hover:text-black transition-all active:scale-95">
            Sign up
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pt-6 md:pt-10 pb-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-6 items-center">
        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-screen flex items-center justify-center overflow-hidden select-none z-0" aria-hidden>
          <span style={anton} className="text-[24vw] leading-none text-white/[0.06] whitespace-nowrap animate-[heroDrift_16s_ease-in-out_infinite_alternate]">STOREIG</span>
        </div>
        <div className="pointer-events-none absolute -top-32 -left-24 w-[55vw] h-[55vw] rounded-full blur-[130px] opacity-25 z-0 animate-[aurora_15s_ease-in-out_infinite_alternate]" style={{ background: `radial-gradient(circle, ${LIME}, transparent 62%)` }} aria-hidden />
        <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.05] mix-blend-soft-light" style={{ backgroundImage: GRAIN }} aria-hidden />
        <motion.div className="relative z-10" variants={stagger} initial="hidden" animate="show">
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6" data-testid="hero-eyebrow">
            <span className="h-2 w-2 rounded-full animate-[igPulse_3s_ease-in-out_infinite]" style={{ background: LIME }} />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/60">{content.hero_eyebrow}</span>
          </motion.div>
          <motion.h1 variants={fadeUp} style={anton} className="text-[19vw] sm:text-[15vw] lg:text-[10rem] leading-[0.82] tracking-tight" data-testid="hero-title">
            STORE<span style={{ color: LIME }} className="animate-[igPulse_3s_ease-in-out_infinite]">IG</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-8 text-2xl md:text-3xl font-light tracking-tight text-white/90 max-w-lg leading-snug">
            {content.hero_title}
          </motion.p>
          <motion.p variants={fadeUp} className="mt-4 text-white/55 max-w-md leading-relaxed" data-testid="hero-subtitle">{content.hero_subtitle}</motion.p>
          <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
            <button onClick={scrollStores} data-testid="hero-explore"
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-black transition-transform active:scale-95 hover:brightness-105 shadow-[0_0_35px_-8px_#ccff00]"
              style={{ background: LIME }}>
              {content.cta_primary_label} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => setSignupOpen(true)}
              className="inline-flex items-center rounded-full px-8 py-4 text-sm font-bold border border-white/30 hover:border-white hover:bg-white hover:text-black transition-all active:scale-95">
              {content.cta_secondary_label}
            </button>
          </motion.div>
          {!loading && stores.length > 0 && (
            <motion.div variants={fadeUp} className="mt-9 flex items-center gap-3 text-sm text-white/50">
              <div className="flex -space-x-3">
                {stores.slice(0, 4).map((s) => (
                  <img key={s.id} src={imgUrl(s.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover ring-2 ring-black bg-zinc-800" />
                ))}
              </div>
              <span><b className="text-white">{stores.length}</b> live storefronts</span>
            </motion.div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.6 }}
          className="relative z-10 flex justify-end">
          <div className="relative w-full max-w-xl">
            <div className="absolute -inset-6 rounded-[2.5rem] blur-3xl opacity-40 animate-[glow_5s_ease-in-out_infinite]" style={{ background: `radial-gradient(circle at 70% 40%, ${LIME}, transparent 65%)` }} />
            <div className="absolute -right-2 top-6 bottom-6 w-2 rounded-full z-10 animate-[glow_5s_ease-in-out_infinite]" style={{ background: LIME }} />
            <div className="relative overflow-hidden rounded-3xl">
              {(content.hero_video || HERO_VIDEO) ? (
                <motion.div
                  initial={{ scale: 1.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}>
                  <PlayOnceVideo
                    withSound
                    src={content.hero_video || HERO_VIDEO}
                    poster={content.hero_image ? imgUrl(content.hero_image) : HERO_FALLBACK}
                    testid="hero-video"
                    className="w-full h-[520px] md:h-[680px] object-cover" />
                </motion.div>
              ) : (
                <img src={content.hero_image ? imgUrl(content.hero_image) : HERO_FALLBACK} alt="Shop the feed"
                  className="w-full h-[520px] md:h-[680px] object-cover contrast-[1.05] animate-[kenburns_18s_ease-in-out_infinite_alternate]" />
              )}
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl" />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Marquee lime strip */}
      <div className="py-4 overflow-hidden" style={{ background: LIME }} data-testid="gradient-stripe">
        <div className="flex gap-8 text-black font-bold uppercase tracking-widest text-sm whitespace-nowrap animate-[marquee_20s_linear_infinite]">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="flex items-center gap-8">Every store, one experience <span>◆</span></span>
          ))}
        </div>
      </div>

      {/* Spotlight — featured shops carousel */}
      {!loading && featuredStores.length > 0 && <Spotlight stores={featuredStores} />}

      {/* New stores */}
      <section id="new-stores" className="max-w-7xl mx-auto px-6 md:px-10 pt-24">
        <div className="mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Fresh drops</span>
          <h2 style={anton} className="text-5xl md:text-6xl tracking-tight mt-2">{content.new_stores_heading || "New stores"}</h2>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="new-stores-grid">
            {newStores.map((s) => <StoreCard key={s.id} s={s} />)}
          </div>
        )}
      </section>

      {/* Find stores by category */}
      <section id="stores" className="max-w-7xl mx-auto px-6 md:px-10 pt-24">
        <div className="mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Browse</span>
          <h2 style={anton} className="text-5xl md:text-6xl tracking-tight mt-2">{content.categories_heading || "Find stores by category"}</h2>
        </div>
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2.5 mb-9" data-testid="category-filter">
            {["All", ...categories].map((c) => (
              <button key={c} onClick={() => setCatFilter(c)} data-testid={`cat-chip-${c}`}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all active:scale-95 ${catFilter === c ? "text-black" : "border border-white/20 text-white/70 hover:border-white/60"}`}
                style={catFilter === c ? { background: LIME } : {}}>
                {c === "All" ? "View All" : c}
              </button>
            ))}
          </div>
        )}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : catStores.length === 0 ? (
          <p className="text-white/40 py-16" data-testid="category-empty">No stores in this category yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="store-directory-grid">
            {catStores.map((s) => <StoreCard key={s.id} s={s} />)}
          </div>
        )}
      </section>

      {/* Easy sign up — movie section */}
      <section id="easy-signup" className="max-w-7xl mx-auto px-6 md:px-10 pt-28" data-testid="easy-signup-section">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-5 rounded-[2rem] blur-3xl opacity-30" style={{ background: `radial-gradient(circle at 40% 40%, ${LIME}, transparent 65%)` }} />
            <div className="relative overflow-hidden rounded-3xl ring-1 ring-white/10">
              <PlayOnceVideo src={content.signup_video || HERO_VIDEO} poster={HERO_FALLBACK}
                testid="signup-video" className="w-full h-[360px] md:h-[460px] object-cover" />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Get started</span>
            <h2 style={anton} className="text-5xl md:text-6xl tracking-tight mt-2">{content.signup_heading || "Easy sign up"}</h2>
            <p className="mt-4 text-white/60 max-w-md leading-relaxed">{content.signup_subtitle || "Launch your storefront in minutes. No code, no hassle — just your brand, online."}</p>
            <div className="mt-8 space-y-5">
              {signupSteps.map((st, i) => (
                <div key={i} className="flex gap-4" data-testid={`signup-step-${i}`}>
                  <div className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center font-bold text-black" style={{ background: LIME }}>{i + 1}</div>
                  <div>
                    <div className="font-semibold text-lg leading-tight">{st.title}</div>
                    <div className="text-white/55 text-sm mt-0.5">{st.text}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setSignupOpen(true)} data-testid="easy-signup-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-black active:scale-95 transition-transform hover:brightness-105 shadow-[0_0_35px_-8px_#ccff00]"
              style={{ background: LIME }}>
              Create your store <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      {content.pricing?.length ? (
        <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-10 pt-28" data-testid="pricing-section">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Pricing</span>
            <h2 style={anton} className="text-5xl md:text-6xl tracking-tight mt-2">{content.pricing_heading}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {content.pricing.map((p, i) => (
              <div key={p.id || `price-${i}`} data-testid={`pricing-box-${i}`}
                className={`rounded-2xl p-8 flex flex-col ${p.highlight ? "bg-[#ccff00] text-black" : "bg-white/[0.04] border border-white/10 text-white"}`}>
                <div className="flex items-center justify-between">
                  <span style={anton} className="text-xl tracking-wide">{p.name}</span>
                  {p.highlight && <span className="text-[10px] font-bold uppercase tracking-widest">Popular</span>}
                </div>
                <div className="mt-4 flex items-end gap-1">
                  <span style={anton} className="text-6xl tracking-tight">{p.price}</span>
                  <span className={`mb-2 text-sm ${p.highlight ? "text-black/60" : "text-white/40"}`}>{p.period}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {(p.features || []).map((f, fi) => (
                    <li key={fi} className={`flex items-center gap-2.5 text-sm ${p.highlight ? "text-black/80" : "text-white/70"}`}>
                      <span className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${p.highlight ? "bg-black text-[#ccff00]" : "bg-[#ccff00] text-black"}`}><Check size={12} /></span> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handlePlan(p, i)} disabled={subscribing === i} data-testid={`pricing-cta-${i}`}
                  className={`mt-8 rounded-full py-3.5 text-sm font-bold transition-transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 ${p.highlight ? "bg-black text-white" : "bg-[#ccff00] text-black"}`}>
                  {subscribing === i && <Loader2 size={15} className="animate-spin" />} {p.cta}
                </button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Reviews */}
      {content.reviews?.length ? <ReviewsV2 heading={content.reviews_heading} reviews={content.reviews} /> : null}

      {/* Footer */}
      <footer className="relative mt-28 border-t border-white/10 overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center leading-none select-none" aria-hidden>
          <span style={anton} className="text-[24vw] text-white/[0.08] translate-y-[22%]">STOREIG</span>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            {/* Brand */}
            <div>
              <div style={anton} className="text-2xl tracking-wide">STORE<span style={{ color: LIME }}>IG</span></div>
              <p className="text-sm text-white/50 mt-3 max-w-xs">{content.footer_tagline}</p>
            </div>
            {/* Explore */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">Explore</div>
              <ul className="space-y-2.5 text-sm text-white/60">
                <li><button onClick={scrollStores} className="hover:text-white transition-colors" data-testid="footer-stores">Stores</button></li>
                <li><a href="#stores" className="hover:text-white transition-colors">Categories</a></li>
                <li><button onClick={() => setSignupOpen(true)} className="hover:text-white transition-colors" data-testid="footer-signup">Become a merchant</button></li>
                <li><Link to="/login" className="hover:text-white transition-colors" data-testid="footer-login">Merchant login</Link></li>
              </ul>
            </div>
            {/* Legal */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">Legal</div>
              <ul className="space-y-2.5 text-sm text-white/60">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.slug}>
                    <Link to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="hover:text-white transition-colors">{l.label}</Link>
                  </li>
                ))}
                <li><button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="hover:text-white transition-colors">Cookie settings</button></li>
              </ul>
            </div>
            {/* More from us */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">More from us</div>
              <ul className="space-y-2.5 text-sm text-white/60">
                <li><a href="https://tiktokwebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-tiktok" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">TikTokWebstore.com <ArrowUpRight size={13} /></a></li>
                <li><a href="https://wawebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-wa" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">WAwebstore.com <ArrowUpRight size={13} /></a></li>
              </ul>
            </div>
          </div>

          {/* Independent platform note */}
          <div className="mt-12 rounded-2xl border border-white/25 bg-white/[0.07] p-6" data-testid="footer-affiliation-note">
            <div className="flex items-center gap-2 text-white/85 mb-2">
              <Info size={17} strokeWidth={2} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Independent platform</span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-3xl font-medium" data-testid="footer-disclaimer">{content.disclaimer || DEFAULT_DISCLAIMER}</p>
          </div>

          <div className="mt-10 text-center">
            <div className="text-xs text-white/40">© {new Date().getFullYear()} StoreIG. All rights reserved.</div>
            <div className="mt-6 flex flex-col items-center gap-2" data-testid="footer-brand">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">A brand by</span>
              <a href={content.brand_url || "#"} target="_blank" rel="noreferrer" aria-label={content.brand_name || "Brand"}>
                {content.brand_logo_url
                  ? <img src={imgUrl(content.brand_logo_url)} alt={content.brand_name || "Brand"} data-testid="footer-brand-logo" className="h-14 w-auto opacity-90 hover:opacity-100 transition-opacity" />
                  : <span data-testid="footer-brand-name" style={anton} className="text-2xl tracking-wide">{content.brand_name}</span>}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Signup dialog */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="signup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Join StoreIG</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500 -mt-2">Tell us about your brand and we'll help you launch your store.</DialogDescription>
          <SignupForm onSuccess={() => setSignupOpen(false)} />
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes kenburns { from { transform: scale(1); } to { transform: scale(1.12); } }
        @keyframes glow { 0%,100% { opacity: 0.35; } 50% { opacity: 0.7; } }
        @keyframes heroDrift { from { transform: translateX(-2.5%); } to { transform: translateX(2.5%); } }
        @keyframes aurora { 0% { transform: translate(0,0) scale(1); } 100% { transform: translate(10%,8%) scale(1.18); } }
        @keyframes igPulse { 0%,100% { text-shadow: 0 0 0 transparent; filter: drop-shadow(0 0 0 transparent); } 50% { text-shadow: 0 0 42px ${LIME}77; filter: drop-shadow(0 0 12px ${LIME}66); } }
        .v2input{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:inherit}
        .v2input:focus{border-color:#000}
      `}</style>
    </div>
  );
}

const Spotlight = ({ stores }) => {
  const [i, setI] = useState(0);
  const n = stores.length;
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setI((x) => (x + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);
  if (!n) return null;
  const go = (d) => setI((x) => (x + d + n) % n);
  const s = stores[i];
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16" data-testid="spotlight">
      <div className="flex items-center gap-3 mb-6">
        <span className="h-2 w-2 rounded-full animate-[igPulse_3s_ease-in-out_infinite]" style={{ background: LIME }} />
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/50">Spotlight · Featured shops</span>
      </div>
      <div className="relative overflow-hidden rounded-3xl ring-1 ring-white/10 h-[440px] md:h-[520px]">
        <AnimatePresence mode="wait">
          <motion.div key={s.handle} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0">
            {s.banner_url ? (
              <img src={imgUrl(s.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${s.accent_color}, #111)` }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
            <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
              <div className="flex items-end gap-5">
                <div className="p-[2.5px] rounded-full shrink-0" style={{ background: `linear-gradient(135deg, ${s.accent_color}, #0002, ${s.accent_color})` }}>
                  <div className="bg-black p-[2px] rounded-full">
                    <img src={imgUrl(s.logo_url)} alt={s.name} className="h-20 w-20 md:h-24 md:w-24 rounded-full object-cover bg-zinc-800" />
                  </div>
                </div>
                <div className="min-w-0">
                  {s.category ? <span className="inline-flex text-[11px] font-bold uppercase tracking-widest rounded-full px-3 py-1 text-black mb-3" style={{ background: LIME }}>{s.category}</span> : null}
                  <h3 style={anton} className="text-4xl md:text-6xl tracking-tight leading-none">{s.name}</h3>
                  <p className="text-white/70 mt-2 max-w-lg line-clamp-2">{s.tagline}</p>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <Link to={`/${s.handle}`} data-testid={`spotlight-visit-${s.handle}`}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-black active:scale-95 transition-transform hover:brightness-105 shadow-[0_0_35px_-8px_#ccff00]" style={{ background: LIME }}>
                  Visit store <ArrowUpRight size={16} />
                </Link>
                <span className="text-sm text-white/50">{s.product_count} products</span>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        {n > 1 && (
          <>
            <button onClick={() => go(-1)} data-testid="spotlight-prev" aria-label="Previous"
              className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 backdrop-blur ring-1 ring-white/20 flex items-center justify-center hover:bg-black/60 transition-colors active:scale-90"><ChevronLeft size={20} /></button>
            <button onClick={() => go(1)} data-testid="spotlight-next" aria-label="Next"
              className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 backdrop-blur ring-1 ring-white/20 flex items-center justify-center hover:bg-black/60 transition-colors active:scale-90"><ChevronRight size={20} /></button>
            <div className="absolute top-5 right-5 flex gap-2">
              {stores.map((_, idx) => (
                <button key={idx} onClick={() => setI(idx)} data-testid={`spotlight-dot-${idx}`} aria-label={`Go to slide ${idx + 1}`}
                  className={`h-2 rounded-full transition-all ${idx === i ? "w-7" : "w-2 bg-white/40 hover:bg-white/70"}`} style={idx === i ? { background: LIME } : {}} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const PlayOnceVideo = ({ src, poster, className, withSound = false, testid }) => {
  const ref = useRef(null);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);
  const replay = () => { const v = ref.current; if (!v) return; v.currentTime = 0; setEnded(false); v.play().catch(() => {}); };
  const toggleSound = () => { const v = ref.current; if (!v) return; const nx = !muted; v.muted = nx; if (!nx) v.volume = 1; setMuted(nx); };
  return (
    <div className="relative">
      <video ref={ref} src={src} poster={poster} autoPlay muted={muted} playsInline
        onEnded={() => setEnded(true)} className={className} data-testid={testid} />
      {ended && (
        <button onClick={replay} data-testid={testid ? `${testid}-replay` : "video-replay"} aria-label="Replay video"
          className="absolute inset-0 flex items-center justify-center bg-black/45 backdrop-blur-[2px] group transition-colors">
          <span className="h-16 w-16 rounded-full flex items-center justify-center text-black shadow-lg transition-transform group-hover:scale-110" style={{ background: LIME }}>
            <Play size={26} className="ml-1 fill-black" />
          </span>
        </button>
      )}
      {withSound && !ended && (
        <button onClick={toggleSound} data-testid="hero-sound-toggle" aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute bottom-4 right-4 z-20 h-11 w-11 rounded-full bg-black/50 backdrop-blur-md ring-1 ring-white/25 flex items-center justify-center text-white hover:bg-black/70 active:scale-90 transition-all">
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      )}
    </div>
  );
};

const StoreCard = ({ s }) => (
  <Link to={`/${s.handle}`} data-testid={`store-card-${s.handle}`}
    className="group block bg-white text-black rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300">
    <div className="h-24 relative" style={{ background: s.banner_url ? undefined : `linear-gradient(135deg, ${s.accent_color}, #111)` }}>
      {s.banner_url && <img src={imgUrl(s.banner_url)} alt="" className="w-full h-full object-cover" />}
      {s.featured && (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1 text-black" style={{ background: LIME }} data-testid={`featured-badge-${s.handle}`}>
          <Star size={10} className="fill-black" /> Featured
        </span>
      )}
    </div>
    <div className="relative z-10 px-6 pb-6 -mt-9">
      <div className="flex items-end justify-between">
        <div className="p-[2.5px] rounded-full" style={{ background: `linear-gradient(135deg, ${s.accent_color}, #0002, ${s.accent_color})` }}>
          <div className="bg-white p-[2px] rounded-full">
            <img src={imgUrl(s.logo_url)} alt={s.name} className="h-16 w-16 rounded-full object-cover bg-zinc-100" />
          </div>
        </div>
        <ArrowUpRight className="text-zinc-300 group-hover:text-black transition-colors mb-2" />
      </div>
      <div style={anton} className="text-xl tracking-wide mt-3">{s.name}</div>
      <div className="text-sm text-zinc-400">@{s.handle}</div>
      <p className="text-zinc-500 mt-2 text-sm leading-relaxed line-clamp-2">{s.tagline}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {s.category ? <span className="inline-flex text-xs font-semibold rounded-full px-3 py-1 bg-zinc-100 text-zinc-600">{s.category}</span> : null}
        <span className="inline-flex text-xs font-bold rounded-full px-3 py-1 bg-black text-white">{s.product_count} products</span>
      </div>
    </div>
  </Link>
);

const SField = ({ label, children }) => (
  <div>
    <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);

const ReviewsV2 = ({ heading, reviews }) => {
  const [idx, setIdx] = useState(0);
  const go = (d) => setIdx((i) => (i + d + reviews.length) % reviews.length);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);
  const r = reviews[idx];
  return (
    <section className="max-w-4xl mx-auto w-full px-6 pt-28" data-testid="reviews-section">
      <div className="text-center mb-12">
        <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Wall of love</span>
        <h2 style={anton} className="text-5xl md:text-6xl tracking-tight mt-2">{heading}</h2>
      </div>
      <div className="relative min-h-[220px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}
            className="border border-white/10 bg-white/[0.04] rounded-3xl px-8 md:px-12 py-10 text-center w-full" data-testid="review-slide">
            <div className="flex justify-center gap-1 mb-5">
              {[...Array(Math.max(0, Math.min(5, r.rating || 5)))].map((_, i) => (
                <Star key={i} size={18} style={{ fill: LIME, stroke: LIME }} />
              ))}
            </div>
            <p className="font-light text-2xl md:text-3xl leading-snug tracking-tight">"{r.text}"</p>
            <div className="mt-7 flex items-center justify-center gap-3">
              <div className="h-11 w-11 rounded-full flex items-center justify-center text-black font-bold" style={{ background: LIME }}>{(r.name || "?").charAt(0)}</div>
              <div className="text-left">
                <div className="font-semibold">{r.name}</div>
                <div className="text-white/40 text-sm">{r.handle}</div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-center gap-4 mt-8">
        <button onClick={() => go(-1)} data-testid="review-prev" className="h-11 w-11 rounded-full border border-white/20 hover:border-white flex items-center justify-center transition-colors active:scale-95"><ChevronLeft size={18} /></button>
        <div className="flex gap-2">
          {reviews.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`review-dot-${i}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-7" : "w-2 bg-white/25"}`} style={i === idx ? { background: LIME } : {}} />
          ))}
        </div>
        <button onClick={() => go(1)} data-testid="review-next" className="h-11 w-11 rounded-full border border-white/20 hover:border-white flex items-center justify-center transition-colors active:scale-95"><ChevronRight size={18} /></button>
      </div>
    </section>
  );
};
